/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useMemo, useRef, useState, ElementRef } from "react";

import "devextreme/dist/css/dx.light.css";
import { gridStyles } from "./Auditrail.styles";

import DataGrid, {
  Column,
  Paging,
  Pager,
  RemoteOperations,
  // HeaderFilter, // <- descomenta si quieres filtros por lista en encabezado
  // FilterRow,    // <- descomenta si quieres fila de filtros en columnas
  SearchPanel,
  FilterRow,
  HeaderFilter,
} from "devextreme-react/data-grid";
import CustomStore from "devextreme/data/custom_store";
import type { LoadOptions } from "devextreme/data/load_options";
import { apiService } from "../../services/apiService";
import notify from "devextreme/ui/notify";
import { Button } from "devextreme-react";
import { useNavigate } from "react-router-dom";

// ================= Tipos =================
interface IAuditLog {
  id: string;
  action: string;
  entityName: string;
  entityId: string;
  userName: string;
  timestamp: string;
  details: string;
  nomina: string;
  sucursalNum: string | null;
  sucursalName: string | null;
}

interface IPagination {
  currentPage: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  // (opcional) si tu API lo tiene:
  totalRecordsFiltered?: number;
}

interface IApiResponse {
  data: IAuditLog[];
  pagination: IPagination;
  message: string;
  status: number;
}

// Endpoints que muestra tu Swagger:
const API_BASE = "/api/Acetileno/Audit/Filter"; // usaremos /page/{pageNumber}/{pageSize}

// ============== Utilidades para mapear filtros/sorts ==============
/** Helpers fecha */
const pad = (n: number) => String(n).padStart(2, "0");
const todayYMD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const addDays = (d: Date, delta: number) => new Date(d.getTime() + delta * 24 * 60 * 60 * 1000);
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toStartOfDay = (yyyyMMdd: string) => `${yyyyMMdd}`;
const toEndOfDay = (yyyyMMdd: string) => `${yyyyMMdd}`;

/** Sort DevExtreme -> { sortField, sortOrder } (primer criterio) */
function mapSort(loadOptions: LoadOptions): { sortField?: string; sortOrder?: "asc" | "desc" } {
  const sort = (loadOptions as any).sort as Array<{ selector: string; desc?: boolean }> | undefined;
  if (Array.isArray(sort) && sort.length > 0) {
    const s0 = sort[0];
    return {
      sortField: s0.selector,
      sortOrder: s0.desc ? "desc" : "asc",
    };
  }
  return {};
}

interface ColumnFilterParams {
  userName?: string;
  action?: string;
  details?: string;
  nomina?: string;
  sucursalName?: string;
  startDate?: string;
  endDate?: string;
}

const sanitizeFilterValue = (value: unknown): string => {
  if (value == null) return "";
  return String(value).replace(/^=\s*/, "").trim();
};

const extractDateValue = (value: unknown): string => {
  const raw = sanitizeFilterValue(value);
  if (!raw) return "";
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw.slice(0, 10);
  }
  return ymd(parsed);
};

const assignColumnFilter = (
  target: ColumnFilterParams,
  field: string,
  value: string
) => {
  switch (field) {
    case "userName":
      target.userName = value;
      break;
    case "action":
      target.action = value;
      break;
    case "details":
      target.details = value;
      break;
    case "nomina":
      target.nomina = value;
      break;
    case "sucursalName":
      target.sucursalName = value;
      break;
    default:
      break;
  }
};

/** Convierte filtros de columnas DevExtreme a query params del backend */
function mapColumnFilters(loadOptions: LoadOptions): ColumnFilterParams {
  const result: ColumnFilterParams = {};
  const filter = (loadOptions as any).filter;

  const applyCondition = (field: string, operator: string, value: unknown) => {
    if (value == null || value === "") return;

    if (Array.isArray(value)) {
      const values = value.map((item) => sanitizeFilterValue(item)).filter(Boolean);
      if (values.length === 0) return;
      assignColumnFilter(result, field, values[0]);
      return;
    }

    const normalized = sanitizeFilterValue(value);
    if (!normalized) return;

    switch (operator.toLowerCase()) {
      case "=":
      case "<>":
      case "contains":
      case "notcontains":
      case "startswith":
      case "endswith":
        if (field === "timestamp") {
          const dateValue = extractDateValue(normalized);
          if (dateValue) {
            result.startDate = dateValue;
            result.endDate = dateValue;
          }
          break;
        }
        assignColumnFilter(result, field, normalized);
        break;
      case ">=":
      case ">":
        if (field === "timestamp") {
          result.startDate = extractDateValue(normalized);
        } else {
          assignColumnFilter(result, field, normalized);
        }
        break;
      case "<=":
      case "<":
        if (field === "timestamp") {
          result.endDate = extractDateValue(normalized);
        } else {
          assignColumnFilter(result, field, normalized);
        }
        break;
      case "anyof":
        assignColumnFilter(result, field, normalized);
        break;
      default:
        assignColumnFilter(result, field, normalized);
        break;
    }
  };

  const walk = (node: unknown): void => {
    if (!node || !Array.isArray(node)) return;

    if (
      node.length === 3 &&
      typeof node[0] === "string" &&
      node[1] !== "and" &&
      node[1] !== "or"
    ) {
      applyCondition(node[0], String(node[1]), node[2]);
      return;
    }

    node.forEach((item) => {
      if (item !== "and" && item !== "or") {
        walk(item);
      }
    });
  };

  walk(filter);
  return result;
};

/** SearchPanel -> "search" libre */
function mapSearch(loadOptions: LoadOptions): { search?: string } {
  const searchValue = (loadOptions as any).searchValue;
  if (searchValue == null || searchValue === "") return {};
  return { search: String(searchValue) };
}

const BitacoraGrid: FC = () => {
  const gridSearchRef = useRef("");
  const dataGridRef = useRef<ElementRef<typeof DataGrid>>(null);

  const handleGridOptionChanged = (e: { fullName?: string; value?: string }) => {
    if (e.fullName === "searchPanel.text") {
      gridSearchRef.current = e.value ?? "";
      dataGridRef.current?.instance()?.getDataSource()?.reload();
    }
  };

  // --------- Filtros controlados (UI) ----------
  const [action, setAction] = useState<string>("");
  const [entityName, setEntityName] = useState<string>("");
  const [userName, setUserName] = useState<string>("");

  // por defecto: últimos 7 días
  const defaultEnd = todayYMD();
  const defaultStart = ymd(addDays(new Date(), -7));
  const navigate = useNavigate(); // ✅ Hook para regresar
  const [startDate, setStartDate] = useState<string>(defaultStart);
  const [endDate, setEndDate] = useState<string>(defaultEnd);

  // Filtros aplicados (solo cambian al dar "Buscar")
  const [appliedFilters, setAppliedFilters] = useState<{
    action?: string;
    entityName?: string;
    userName?: string;
    startDate?: string; // YYYY-MM-DD
    endDate?: string;   // YYYY-MM-DD
  }>({
    action: "",
    entityName: "",
    userName: "",
    startDate: defaultStart,
    endDate: defaultEnd,
  });

  const applyFilters = () => {
    // Validación simple de rango
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      notify({ message: "La fecha inicio no puede ser mayor que la fecha fin.", type: "warning", displayTime: 3000 });
      return;
    }
    setAppliedFilters({
      action: action.trim(),
      entityName: entityName.trim(),
      userName: userName.trim(),
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    });
  };

  /*const clearFilters = () => {
    setAction("");
    setEntityName("");
    setUserName("");
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setAppliedFilters({
      action: "",
      entityName: "",
      userName: "",
      startDate: defaultStart,
      endDate: defaultEnd,
    });
  };*/

  // --------- DataSource con operaciones remotas ----------
  const dataSource = useMemo(() => {
    return new CustomStore<IAuditLog>({
      key: "id",
      load: async (loadOptions: LoadOptions) => {
        // Página/Tamaño
        const pageSize = loadOptions.take || 10;
        const pageNumber = (loadOptions.skip || 0) / pageSize + 1;

        // Sort/Filter/Search mapeados del grid
        const sortParams = mapSort(loadOptions);
        const columnFilters = mapColumnFilters(loadOptions);
        const searchParams = mapSearch(loadOptions);
        const searchTerm = (
          searchParams.search ??
          gridSearchRef.current ??
          ""
        ).trim();

        // Filtros “aplicados” desde la barra superior
        const qp: Record<string, any> = {
          pageNumber,
          pageSize,
          ...sortParams,
        };

        if (searchTerm) {
          qp.search = searchTerm;
        }

        // Barra superior
        if (appliedFilters.action) qp.action = appliedFilters.action;
        if (appliedFilters.entityName) qp.entityName = appliedFilters.entityName;
        if (appliedFilters.userName) qp.userName = appliedFilters.userName;
        if (appliedFilters.startDate) qp.startDate = toStartOfDay(appliedFilters.startDate);
        if (appliedFilters.endDate) qp.endDate = toEndOfDay(appliedFilters.endDate);

        // Filtros de fila: solo si no hay búsqueda global (el SearchPanel
        // genera un OR por columna que aquí se convertiría en AND).
        if (!searchTerm) {
          if (columnFilters.userName) qp.userName = columnFilters.userName;
          if (columnFilters.action) qp.action = columnFilters.action;
          if (columnFilters.details) qp.details = columnFilters.details;
          if (columnFilters.nomina) qp.nomina = columnFilters.nomina;
          if (columnFilters.sucursalName) qp.sucursalName = columnFilters.sucursalName;
          if (columnFilters.startDate) qp.startDate = toStartOfDay(columnFilters.startDate);
          if (columnFilters.endDate) qp.endDate = toEndOfDay(columnFilters.endDate);
        }

        // Llamada a /api/Audit/page/{pageNumber}/{pageSize} con filtros como query
        const url = `${API_BASE}`;
        const resp = await apiService.getRaw<IApiResponse>(url, { params: qp });

        const items = resp.data;
        const totalCount =
          resp.pagination.totalRecordsFiltered ?? resp.pagination.totalRecords;

        return {
          data: items,
          totalCount,
        };
      },
    });
  }, [appliedFilters]); // <- se recrea solo cuando das "Buscar" o "Limpiar"

  return (
    <>
      <style>{gridStyles}</style>

      <div className="bitacora-container">
        <h2>Bitácora de Acciones</h2>
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}
      >
        <Button
          icon="chevronleft"
          text="Regresar"
          stylingMode="outlined"
          type="default"
          onClick={() => navigate(-1)} // ← vuelve a la pestaña anterior
        />
      </div>
        {/* -------- Barra de filtros (como en Swagger) -------- */}
        <div className="filters-bar">
          <div className="filter-field">
            <label>Acción</label>
            <input
              className="filter-input"
              type="text"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              placeholder="e.g. Create, Update…"
            />
          </div>

          {/*<div className="filter-field">
            <label>Entidad</label>
            <input
              className="filter-input"
              type="text"
              value={entityName}
              onChange={(e) => setEntityName(e.target.value)}
              placeholder="e.g. Pedido, Producto…"
            />
          </div>*/}

          <div className="filter-field">
            <label>Usuario</label>
            <input
              className="filter-input"
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder="e.g. jperez"
            />
          </div>

          <div className="filter-field">
            <label>Fecha inicio</label>
            <input
              className="filter-input"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={endDate || undefined}
            />
          </div>

          <div className="filter-field">
            <label>Fecha fin</label>
            <input
              className="filter-input"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || undefined}
            />
          </div>

          <div className="filter-actions">
            <button className="btn primary" onClick={applyFilters}>Buscar</button>
          </div>
        </div>

        <DataGrid
          ref={dataGridRef}
          dataSource={dataSource}
          onOptionChanged={handleGridOptionChanged}
          showBorders
          wordWrapEnabled
          allowColumnResizing
          height={600}
          className="custom-grid"
        >
          {/* Operaciones remotas para paginado coherente */}
          <RemoteOperations
            paging={true}
            filtering={true}
            sorting={true}
            summary={false}
            grouping={false}
          />

          <FilterRow visible={true} />
          <HeaderFilter visible={true} />

          <SearchPanel
            visible={true}
            highlightCaseSensitive={false}
            placeholder="Buscar..."
            width={240}
          />

          {/* Columnas */}
          <Column dataField="userName" caption="Usuario" width={150} />
          <Column dataField="action" caption="Acción" width={160} />
          {/*<Column dataField="entityName" caption="Entidad" width={160} />*/}
          <Column dataField="details" caption="Detalles" minWidth={300} />
          <Column
            dataField="timestamp"
            caption="Fecha y Hora"
            dataType="datetime"
            format="yyyy-MM-dd HH:mm:ss"
            width={180}
            allowSearch={false}
          />
          <Column dataField="nomina" caption="Nómina" width={110} />
          <Column dataField="sucursalName" caption="Sucursal" width={170} />

          {/* Paginación */}
          <Paging defaultPageSize={50} />
          <Pager
            visible
            allowedPageSizes={[50]}
            showPageSizeSelector
            showInfo
            showNavigationButtons
          />
        </DataGrid>
      </div>
    </>
  );
};

export default BitacoraGrid;
